import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import CircularLoader from "../../../components/Loaders/CircularLoader";
import { EMAIL_ORANGE_BACKGROUND_IMAGE } from "../../../utils/constants/assets";
import { STRINGS } from "../strings";
import classes from "./Crm.module.css";
import { BRAND } from "../../../utils/constants/colors";
import { fontWeight } from "../../../utils/constants/UI";
import { Button } from "../../../components/Buttons";
import TypographyInline from "../../../components/Typography/TypographyInline";
import SkeletonLoaderCRMScreens from "./components/SkeletonLoaderCRMScreens";
import ContactDetailEmptyScreen from "./components/ContactDetailEmptyScreen";
import EmailLogList from "./components/EmailLogList";
import EmailSentList from "./components/EmailSentList";
import CampaignThreadCard from "./components/CampaignThreadCard";
import LogEmailModal from "./components/LogEmailModal";
import EmailSendModal from "./components/EmailSendModal";
import EnrollInSequenceDrawer from "./components/EnrollInSequenceDrawer";

// States that mean the contact has LEFT the campaign for good. Anything else -
// including paused - keeps Compose Email + Enroll in Sequence blocked.
//
// PAUSING DOES NOT UNBLOCK. A pause is temporary: resuming re-arms every row, so a
// contact enrolled in a sequence while the campaign sat paused got contacted twice
// the moment it resumed, once per engine. Only unenrolling clears the block.
// Allowlisting what unblocks (rather than listing what blocks) means an unrecognised
// state holds the block instead of silently opening a second sending channel.
const CAMPAIGN_DONE_STATES = new Set([
  "completed",
  "finished",
  "stopped",
  "ended",
  "cancelled",
  "canceled",
]);

const hasActiveEnrollment = (campaigns = []) =>
  campaigns.some((c) => {
    // An unenrolled row keeps its last live state, so without this it would keep
    // blocking Compose for a contact who is no longer in the campaign.
    if (c?.unenrolled === true) return false;
    const s = (c?.state || "")
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");
    return !CAMPAIGN_DONE_STATES.has(s);
  });

// Same rule as ContactDetailScreen: an enrollment that has sent all of its steps is finished even
// though its status is still 'active', because nothing ever writes a terminal status. Without this
// the Compose button stayed blocked forever on a completed sequence.
const isExhaustedEnrollment = (s) =>
  Number(s?.total_steps) > 0 && Number(s?.sent_count) >= Number(s?.total_steps);

const hasActiveSequenceEnrollment = (sequences = []) =>
  sequences.some(
    (s) =>
      ["active", "paused"].includes((s?.status || "").toString().toLowerCase()) &&
      !isExhaustedEnrollment(s)
  );

const BLOCKED_TOOLTIP =
  "Contact is still enrolled in a marketing campaign or sequence - unenroll first. Pausing is not enough.";

const ContactDetailEmailScreen = ({
  contactDetails,
  contactId,
  getEmailLogList = () => {},
  emailLogListData,
  screenLoading,
  totalEmails,
  activeCampaigns = [],
  activeSequences = [],
  refreshActiveEnrollments = () => {},
}) => {
  const blocked =
    hasActiveEnrollment(activeCampaigns) ||
    hasActiveSequenceEnrollment(activeSequences);
  const [openLogEmailModal, setOpenLogEmailModal] = useState(false);
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const [openEnrollSequenceModal, setOpenEnrollSequenceModal] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  // True while a scroll-triggered "load more" page fetch is in flight. Drives
  // the bottom spinner and (via the ref) prevents a second page from firing
  // before the first one finishes.
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const [clickedEmail, setClickedEmail] = useState({});
  const [replyType, setReplyType] = useState("");

  const emptyScreenButtons = [
    {
      id: STRINGS.LOG_EMAIL_SMALL,
      label: STRINGS.LOG_EMAIL,
      variant: "ghost",
      disabled: !Object.keys(contactDetails || {}).length,
      onClick: () => {
        setOpenLogEmailModal(true);
      },
    },
    {
      id: STRINGS.SEND_EMAIL_SMALL,
      label: STRINGS.COMPOSE_EMAIL,
      variant: "primary",
      disabled: !Object.keys(contactDetails || {}).length || blocked,
      onClick: () => {
        if (blocked) return;
        setOpenSendEmailModal(true);
      },
    },
    {
      id: "enroll_in_sequence",
      label: STRINGS.ENROLL_IN_SEQUENCE,
      variant: "primary",
      disabled: !Object.keys(contactDetails || {}).length || blocked,
      onClick: () => {
        if (blocked) return;
        setOpenEnrollSequenceModal(true);
      },
    },
  ];

  const closeLogEmailModal = () => {
    setOpenLogEmailModal(false);
  };

  const closeSendEmailModal = () => {
    setOpenSendEmailModal(false);
    setClickedEmail({});
  };

  const onScrolllEmailListHandler = async () => {
    const scrollableDiv = document.getElementsByClassName(
      STRINGS.ALL_EMAIL_LIST
    )[0];
    const isAtBottom =
      scrollableDiv.scrollTop >=
      scrollableDiv.scrollHeight - scrollableDiv.clientHeight;
    // Guard against firing a second page while the first is still loading -
    // the scroll event fires repeatedly near the bottom.
    if (
      isAtBottom &&
      !loadingMoreRef.current &&
      totalEmails > emailLogListData?.length
    ) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      const nextPage = pageCount + 1;
      setPageCount(nextPage);
      try {
        await getEmailLogList(true, nextPage);
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    getEmailLogList();
  }, [contactDetails]);

  return (
    <div
      id="modal_body"
      className={`${classes.emailScreenMainContainer} ${STRINGS.ALL_EMAIL_LIST}`}
      onScroll={onScrolllEmailListHandler}
    >
      {/* Loader of Email Screen */}
      {screenLoading ? (
        <SkeletonLoaderCRMScreens
          loaderHeight="42px"
          accordionLengths={10}
          buttonsLength={2}
        />
      ) : (
        <>
          {/* Email Log Data Length Check */}
          {emailLogListData?.length ? (
            <div className={classes.emailLogListContainer}>
              <div className={classes.labelButtonContainer}>
                <div>
                  <TypographyInline
                    color={BRAND.SECONDARY}
                    label={STRINGS.ALL_EMAILS}
                    size={"sm"}
                    fontWeight={fontWeight.SEMIBOLD}
                  />
                </div>
                <div className={classes.emailLogButtonContainer}>
                  <Button
                    label={STRINGS.LOG_EMAIL}
                    size={"sm36"}
                    variant={"ghost"}
                    onClick={() => setOpenLogEmailModal(true)}
                  />
                  <Tooltip title={blocked ? BLOCKED_TOOLTIP : ""} arrow>
                    <span>
                      <Button
                        label={STRINGS.COMPOSE_EMAIL}
                        size={"sm36"}
                        variant={"primary"}
                        disabled={blocked}
                        onClick={() => {
                          if (blocked) return;
                          setOpenSendEmailModal(true);
                        }}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title={blocked ? BLOCKED_TOOLTIP : ""} arrow>
                    <span>
                      <Button
                        label={STRINGS.ENROLL_IN_SEQUENCE}
                        size={"sm36"}
                        variant={"primary"}
                        disabled={blocked}
                        onClick={() => {
                          if (blocked) return;
                          setOpenEnrollSequenceModal(true);
                        }}
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
              {emailLogListData?.map((logData, index) => {
                // For campaign emails, look up the parent campaign's state
                // so EmailSentList can hide the header date when the
                // campaign hasn't been scheduled yet (Ready To Run / draft /
                // queued). Otherwise we'd show whatever timestamp the
                // record was created with, which the user reads as a real
                // send time.
                const parentCampaign = (activeCampaigns || []).find(
                  (c) => c?.campaign_id === logData?.campaign_id,
                );
                const parentState = String(
                  parentCampaign?.state || "",
                ).trim().toLowerCase();
                const isPreScheduled =
                  !!logData?.campaign_id &&
                  ["ready to run", "ready_to_run", "draft", "queued"].includes(
                    parentState,
                  );
                // A campaign is ONE row now, its steps and the contact's replies inside it.
                // Rendered by its own card, which reuses EmailSentList for each item so chips,
                // reply bodies and comments behave exactly as they did when these were top-level
                // rows.
                if (logData?.type === "campaign_thread") {
                  return (
                    <CampaignThreadCard
                      key={`thread${logData?._id || index}`}
                      emailData={logData}
                      contactDetails={contactDetails}
                      getEmailLogList={getEmailLogList}
                      expanded={!index}
                      openSendEmailModal={openSendEmailModal}
                      setOpenSendEmailModal={setOpenSendEmailModal}
                      setClickedEmail={setClickedEmail}
                      setReplyType={setReplyType}
                      isPreScheduled={isPreScheduled}
                    />
                  );
                }
                return logData?.type === "log" ? (
                  <div key={`log${index}`}>
                    <EmailLogList
                      id={`log${index}`}
                      logData={logData}
                      contactDetails={contactDetails}
                      getEmailLogList={getEmailLogList}
                      expanded={!index}
                    />
                  </div>
                ) : (
                  <EmailSentList
                    key={`mail${logData?._id || index}`}
                    id={`log${index}`}
                    emailData={logData}
                    contactDetails={contactDetails}
                    getEmailLogList={getEmailLogList}
                    expanded={!index}
                    openSendEmailModal={openSendEmailModal}
                    setOpenSendEmailModal={setOpenSendEmailModal}
                    setClickedEmail={setClickedEmail}
                    setReplyType={setReplyType}
                    isPreScheduled={isPreScheduled}
                  />
                );
              })}
              {/* Bottom spinner shown while a "load more" page is fetching. */}
              {loadingMore && (
                <div className={classes.emailLoadMoreLoader}>
                  <CircularLoader size={28} />
                </div>
              )}
            </div>
          ) : (
            // Email Log Empty Screen.
            <div className={classes.emptyEmailDetailMainContainer}>
              <ContactDetailEmptyScreen
                src={EMAIL_ORANGE_BACKGROUND_IMAGE}
                label={STRINGS.EMAIL_SCREEN_EMPTY_DESC}
                buttonItems={emptyScreenButtons}
              />
            </div>
          )}
        </>
      )}

      {/* Create Log Email Modal */}
      <LogEmailModal
        open={openLogEmailModal}
        close={closeLogEmailModal}
        contactDetails={contactDetails}
        contactId={contactId}
        getEmailLogList={getEmailLogList}
      />

      {/* Enroll in Sequence Drawer */}
      <EnrollInSequenceDrawer
        open={openEnrollSequenceModal}
        onClose={() => setOpenEnrollSequenceModal(false)}
        contactDetails={contactDetails}
        contactId={contactId}
        // Refresh the emails list after a successful schedule so the
        // newly-queued touches show up without the user having to switch
        // tabs and back, and refetch the active campaign/sequence panel so
        // it reflects the new enrollment instead of a stale state.
        onEnrolled={() => {
          getEmailLogList();
          refreshActiveEnrollments();
        }}
      />

      {/* Create/Send Email Modal */}
      {openSendEmailModal && (
        <EmailSendModal
          open={openSendEmailModal}
          close={closeSendEmailModal}
          contactDetails={contactDetails}
          contactId={contactId}
          getEmails={getEmailLogList}
          data={clickedEmail}
          setPageCount={setPageCount}
          replyType={replyType}
          postAction={getEmailLogList}
        />
      )}
    </div>
  );
};

export default ContactDetailEmailScreen;
